import React from 'react';
import './MarkdownMessage.css';

/**
 * Renders inline markdown: **bold**, __bold__, *italic*, _italic_, `code`
 * Returns an array of strings and React elements.
 */
function renderInline(text, prefix) {
  if (!text) return [];
  const parts = [];
  // Order matters: ** before * so bold isn't consumed as two italics
  const regex = /(`[^`\n]+`|\*\*[^*\n]+\*\*|__[^_\n]+__|_[^_\n]+_|\*[^*\n]+\*)/g;
  let last = 0;
  let n = 0;
  let m;
  while ((m = regex.exec(text)) !== null) {
    if (m.index > last) parts.push(text.slice(last, m.index));
    const t = m[0];
    const k = `${prefix}_${n++}`;
    if (t[0] === '`') {
      parts.push(<code key={k} className="md-ic">{t.slice(1, -1)}</code>);
    } else if (t.startsWith('**') || t.startsWith('__')) {
      parts.push(<strong key={k}>{t.slice(2, -2)}</strong>);
    } else {
      parts.push(<em key={k}>{t.slice(1, -1)}</em>);
    }
    last = m.index + t.length;
  }
  if (last < text.length) parts.push(text.slice(last));
  return parts.length ? parts : [text];
}

/**
 * Lightweight markdown-to-React renderer for LLM responses.
 * Handles: headings, bullet lists, numbered lists, code blocks,
 * blockquotes, bold, italic, inline code, horizontal rules.
 * No external dependencies.
 */
export default function MarkdownMessage({ content }) {
  if (!content) return null;

  // Normalise line endings
  const text = content.replace(/\r\n?/g, '\n');
  const lines = text.split('\n');
  const blocks = [];
  let i = 0;
  let key = 0;

  while (i < lines.length) {
    const line = lines[i];
    const k = key++;

    // ── Fenced code block ────────────────────────────────────────────
    if (/^\s*```/.test(line)) {
      const lang = line.replace(/^\s*```/, '').trim();
      i++;
      const codeLines = [];
      while (i < lines.length && !/^\s*```\s*$/.test(lines[i])) {
        codeLines.push(lines[i]);
        i++;
      }
      i++; // skip closing ```
      blocks.push(
        <pre key={k} className="md-code-block">
          <code className={lang ? `language-${lang}` : ''}>{codeLines.join('\n')}</code>
        </pre>
      );
      continue;
    }

    // ── Heading ───────────────────────────────────────────────────────
    const hm = line.match(/^(#{1,4})\s+(.*)/);
    if (hm) {
      // Map # → h3, ## → h4 … so it doesn't overpower the UI chrome
      const lvl = Math.min(hm[1].length + 2, 6);
      const Tag = `h${lvl}`;
      blocks.push(
        <Tag key={k} className="md-heading">
          {renderInline(hm[2], `h${k}`)}
        </Tag>
      );
      i++;
      continue;
    }

    // ── Horizontal rule ───────────────────────────────────────────────
    if (/^[-*_]{3,}\s*$/.test(line.trim())) {
      blocks.push(<hr key={k} className="md-hr" />);
      i++;
      continue;
    }

    // ── Unordered list ────────────────────────────────────────────────
    if (/^[\-\*\+] /.test(line)) {
      const items = [];
      while (i < lines.length && /^[\-\*\+] /.test(lines[i])) {
        items.push(lines[i].replace(/^[\-\*\+] /, ''));
        i++;
      }
      blocks.push(
        <ul key={k} className="md-ul">
          {items.map((it, j) => (
            <li key={j}>{renderInline(it, `ul${k}_${j}`)}</li>
          ))}
        </ul>
      );
      continue;
    }

    // ── Ordered list ──────────────────────────────────────────────────
    if (/^\d+[.)]\s/.test(line)) {
      const items = [];
      while (i < lines.length && /^\d+[.)]\s/.test(lines[i])) {
        items.push(lines[i].replace(/^\d+[.)]\s+/, ''));
        i++;
      }
      blocks.push(
        <ol key={k} className="md-ol">
          {items.map((it, j) => (
            <li key={j}>{renderInline(it, `ol${k}_${j}`)}</li>
          ))}
        </ol>
      );
      continue;
    }

    // ── Blockquote ────────────────────────────────────────────────────
    if (/^> /.test(line)) {
      const bqLines = [];
      while (i < lines.length && /^> /.test(lines[i])) {
        bqLines.push(lines[i].slice(2));
        i++;
      }
      blocks.push(
        <blockquote key={k} className="md-bq">
          {renderInline(bqLines.join(' '), `bq${k}`)}
        </blockquote>
      );
      continue;
    }

    // ── Empty line (paragraph separator) ─────────────────────────────
    if (line.trim() === '') {
      i++;
      continue;
    }

    // ── Paragraph ─────────────────────────────────────────────────────
    // Collect consecutive non-special lines
    const pl = [];
    while (
      i < lines.length &&
      lines[i].trim() !== '' &&
      !/^(#{1,4} |[\-\*\+] |\d+[.)]\s|```|> |[-*_]{3,}\s*$)/.test(lines[i])
    ) {
      pl.push(lines[i]);
      i++;
    }
    if (pl.length) {
      // Join lines; insert <br> between them to preserve intentional hard wraps
      const nodes = pl.flatMap((l, j) =>
        j === 0
          ? renderInline(l, `p${k}_0`)
          : [<br key={`br${k}_${j}`} />, ...renderInline(l, `p${k}_${j}`)]
      );
      blocks.push(<p key={k} className="md-p">{nodes}</p>);
    }
  }

  return <div className="md-content">{blocks}</div>;
}
